const CartItemGroup = require('@/models/CartItemGroup');

/**
 * Create CartItemGroups from an array of CartLines.
 * - Tickets for the same session will be grouped together.
 * - Sessions will be split up by group booking reference, if applicable.
 * - Everything else goes into a default group.
 *
 * @param {CartLine[]} items
 *
 * @return {CartItemGroup[]}
 */
const createCartItemGroupsFromItems = (items = []) => {
    /**
     * @type {Object<string, CartItemGroup>}
     */
    const itemGroups = {};

    // See also src/js/lib/Orders/createItemGroups.js for how this is done for order line groups
    // when displaying orders.

    for (let i = 0; i < items.length; ++i) {
        const item = items[i];

        // Exclude item option lines - they will be displayed specially within their parent item.
        if (item.isItemOption) {
            continue;
        }

        let groupKey = [
            item.session?.ID || null,
            item.groupBookingReference || null,
        ].join('-');

        if (!itemGroups.hasOwnProperty(groupKey)) {
            const groupType = item.session ? 'session' : 'default';
            const group = new CartItemGroup(groupKey, groupType);
            group.setEvent(item.getEvent());
            group.setSession(item.session);
            group.groupBookingReference = item.groupBookingReference;

            itemGroups[groupKey] = group;
        }

        itemGroups[groupKey].addItem(item);
    }

    const groups = Object.values(itemGroups);

    sortItemGroups(groups);

    return groups;
};

/**
 * Sort the cart item groups:
 * - Session groups should be at the top, sorted by session start time and then by group booking reference.
 * - Other groups (of which there should only be one) should be at the bottom.
 *
 * @param {CartItemGroup[]} groups
 */
const sortItemGroups = (groups) => {
    groups.sort((a, b) => {
        if (a.session && b.session) {
            // Both groups are session groups.

            // 1. Sort by session start time.
            const timeDiff = (a.session.startTime?.getTime() || a.session.ID || 0)
                - (b.session.startTime?.getTime() || b.session.ID || 0);
            if (timeDiff !== 0) {
                return timeDiff;
            }

            // 2. Same session time - sort by presence of booking group name first.
            const hasNameA = !!a.groupBookingReference;
            const hasNameB = !!b.groupBookingReference;

            if (hasNameA !== hasNameB) {
                // Items with a group name come first.
                return hasNameA ? -1 : 1;
            }

            // 3. If both have names (or both lack names), sort alphabetically by name.
            // (If both have no name this won't do anything)
            const nameA = a.groupBookingReference || '';
            const nameB = b.groupBookingReference || '';
            const nameDiff = nameA.localeCompare(nameB);
            if (nameDiff !== 0) {
                return nameDiff;
            }
        } else if (a.session || b.session) {
            // One group is a session group, the other is not - put the session group first.
            return a.session ? -1 : 1;
        }

        // Neither is a session group, so they are both default groups.
        // There should only be one non-session group, but if there are more we don't care about the order.
        return 0;
    });
};

/**
 * Calculate the total number of spaces of all the tickets in a session group.
 *
 * @param {CartItemGroup} cartItemGroup
 *
 * @return {Object<number>}
 */
const countSessionSpacesInGroup = (cartItemGroup) => {
    let totalSessionSpacesInGroup = {};

    cartItemGroup.items.forEach((i) => {
        if (!i.session) {
            return;
        }
        if (!totalSessionSpacesInGroup.hasOwnProperty(i.session.ID)) {
            totalSessionSpacesInGroup[i.session.ID] = 0;
        }
        totalSessionSpacesInGroup[i.session.ID] += i.item.getPeople() * i.quantity;
    });

    return totalSessionSpacesInGroup;
};

/**
 * Returns an array of objects in the format that can be passed to SessionPickerModal representing the quantity
 * of tickets requested in a CartItemGroup.
 *
 * @param {CartItemGroup} cartItemGroup
 *
 * @return {{ticket: Ticket, qty: Number}[]}
 */
const getTicketQtysInGroup = (cartItemGroup) => cartItemGroup.items.map((i) => ({
    ticket: i.item,
    qty: i.quantity,
    lineNumber: i.lineNumber,
}));

module.exports = {
    getTicketQtysInGroup,
    countSessionSpacesInGroup,
    createCartItemGroupsFromItems,
};
